/**
 * Indiana Jerky - Main Theme JavaScript
 * Vanilla ES6+ | No dependencies
 */

/* ============================================================
   Cart API Helpers
   ============================================================ */
class CartAPI {
  static async get() {
    return fetch('/cart.js').then(r => r.json());
  }

  static async add(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    }).then(r => r.json());
  }

  static async change(updates) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    }).then(r => r.json());
  }

  static async update(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    }).then(r => r.json());
  }
}
window.CartAPI = CartAPI;

/* ============================================================
   Event Bus - Cart Updated
   ============================================================ */
document.addEventListener('cart:updated', (e) => {
  const cart = e.detail;
  if (!cart) return;
  updateCartCounts(cart.item_count);
});

function updateCartCounts(count) {
  document.querySelectorAll('[data-cart-count]').forEach(el => {
    el.textContent = count;
    el.setAttribute('aria-label', `${count} items in cart`);
    el.classList.toggle('is-empty', count === 0);
  });
}

/* ============================================================
   Section Rendering Helper
   ============================================================ */
async function fetchSection(sectionId) {
  const url = `${window.location.pathname}?sections=${sectionId}`;
  const response = await fetch(url);
  const data = await response.json();
  return data[sectionId];
}
window.fetchSection = fetchSection;

/* ============================================================
   Header / Mobile Menu
   ============================================================ */
function initHeader() {
  const header = document.querySelector('.header, .headerE');
  if (!header) return;

  /* --- Sticky header with scroll-direction detection --- */
  let lastScrollY = window.scrollY;
  let ticking = false;
  const stickyThreshold = 100;

  function onScroll() {
    if (ticking) return;
    ticking = true;

    requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > stickyThreshold) {
        header.classList.add('header--sticky');

        if (currentScrollY < lastScrollY) {
          // Scrolling up - show header
          header.classList.add('header--visible');
          header.classList.remove('header--hidden');
        } else {
          // Scrolling down - hide header
          header.classList.remove('header--visible');
          header.classList.add('header--hidden');
        }
      } else {
        header.classList.remove('header--sticky', 'header--visible', 'header--hidden');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  /* --- Mobile menu toggle --- */
  const menuToggle = document.querySelector('[data-mobile-menu-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const menuOverlay = document.querySelector('[data-mobile-menu-overlay]');

  if (menuToggle && mobileMenu) {
    menuToggle.addEventListener('click', () => {
      const isOpen = mobileMenu.getAttribute('aria-hidden') === 'false';
      if (isOpen) {
        closeMobileMenu();
      } else {
        openMobileMenu();
      }
    });

    if (menuOverlay) {
      menuOverlay.addEventListener('click', closeMobileMenu);
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileMenu.getAttribute('aria-hidden') === 'false') {
        closeMobileMenu();
      }
    });
  }

  function openMobileMenu() {
    mobileMenu.setAttribute('aria-hidden', 'false');
    mobileMenu.classList.add('is-open');
    menuToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    if (menuOverlay) menuOverlay.classList.add('is-visible');
    mobileMenu.querySelector('a, button')?.focus();
  }

  function closeMobileMenu() {
    mobileMenu.setAttribute('aria-hidden', 'true');
    mobileMenu.classList.remove('is-open');
    menuToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (menuOverlay) menuOverlay.classList.remove('is-visible');
    menuToggle.focus();
  }
}

/* ============================================================
   Dropdown Menus (non-mega)
   ============================================================ */
function initDropdowns() {
  const dropdowns = document.querySelectorAll('[data-dropdown]');

  dropdowns.forEach(dropdown => {
    const trigger = dropdown.querySelector('[data-dropdown-trigger]');
    const panel = dropdown.querySelector('[data-dropdown-panel]');
    if (!trigger || !panel) return;

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';

      // Close all other dropdowns first
      closeAllDropdowns();

      if (!isOpen) {
        trigger.setAttribute('aria-expanded', 'true');
        panel.setAttribute('aria-hidden', 'false');
        panel.classList.add('is-open');
      }
    });

    dropdown.addEventListener('mouseenter', () => {
      trigger.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');
      panel.classList.add('is-open');
    });

    dropdown.addEventListener('mouseleave', () => {
      trigger.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      panel.classList.remove('is-open');
    });
  });

  // Close dropdowns on outside click
  document.addEventListener('click', () => {
    closeAllDropdowns();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAllDropdowns();
  });
}

function closeAllDropdowns() {
  document.querySelectorAll('[data-dropdown]').forEach(dropdown => {
    const trigger = dropdown.querySelector('[data-dropdown-trigger]');
    const panel = dropdown.querySelector('[data-dropdown-panel]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) {
      panel.setAttribute('aria-hidden', 'true');
      panel.classList.remove('is-open');
    }
  });
}

/* ============================================================
   Custom Element Registration Helper
   ============================================================ */
function registerElement(name, constructor) {
  if (!customElements.get(name)) {
    customElements.define(name, constructor);
  }
}
window.registerElement = registerElement;

/* ============================================================
   DOMContentLoaded Init
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initDropdowns();

  // Fetch initial cart count
  CartAPI.get().then(cart => {
    updateCartCounts(cart.item_count);
  }).catch(() => {});
});
